import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Calendar from '../../components/common/Calendar';
import apiClient from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const GuestsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const CalendarSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CalendarInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 1rem;
  line-height: 1.6;
`;

const SearchSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#000000' : '#dee2e6'};
  background: ${props => props.active ? '#000000' : 'white'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #000000;
    background: ${props => props.active ? '#000000' : '#f8f9fa'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const GuestsList = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 12px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const Thead = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const Th = styled.th`
  padding: 0.875rem 0.375rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.7rem;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 0.875rem 0.375rem;
  font-size: 0.75rem;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.7rem;
  }
`;

const NameCell = styled.td`
  padding: 0.875rem 0.375rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const StatusBadge = styled.span<{ status?: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === '승인') return '#d1e7dd';
    if (props.status === '대기') return '#fff3cd';
    if (props.status === '거부') return '#f8d7da';
    return '#dee2e6';
  }};
  color: ${props => {
    if (props.status === '승인') return '#0f5132';
    if (props.status === '대기') return '#856404';
    if (props.status === '거부') return '#842029';
    return '#6c757d';
  }};
  white-space: nowrap;
  display: inline-block;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.6rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.9375rem;
  color: #6c757d;
  font-weight: 600;
`;

const DetailValue = styled.span`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
`;

const EditInput = styled(Input)`
  font-size: 0.9375rem;
  padding: 0.5rem;
  width: 100%;
`;

const EditSelect = styled.select`
  font-size: 0.9375rem;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  width: 100%;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000000;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const DialogBox = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const DialogMessage = styled.p`
  margin: 0 0 1.25rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  white-space: pre-line;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

interface Guest {
  guest_no: number;
  name: string;
  phone: string;
  birth_date?: string; // 생년월일 (YYMMDD)
  gender: string;
  election_status: string;
  selected_date: string;
  apply_date: string;
  status: '대기' | '승인' | '거부';
  invited_by_name?: string; // 회원이 데려온 경우 회원 이름
  is_direct_apply: boolean; // Home에서 직접 신청한 경우 true
  apply_path?: string; // 신청 경로 (사용자 입력)
  apply_count?: number; // 신청 횟수
  fee_required?: boolean; // 비용 필요 여부 (2회차부터)
}

const AdminGuests: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('전체');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Guest>>({});
  const [guests, setGuests] = useState<Guest[]>([]);
  const [schedules, setSchedules] = useState<Array<{ schedule_date: string; schedule_time?: string; location?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<{ type: 'confirm' | 'alert'; message: string } | null>(null);
  const dialogResolveRef = useRef<((value: boolean) => void) | null>(null);

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      dialogResolveRef.current = resolve;
      setDialog({ type: 'confirm', message });
    });
  }, []);

  const showAlert = useCallback((message: string): Promise<void> => {
    return new Promise((resolve) => {
      dialogResolveRef.current = () => resolve();
      setDialog({ type: 'alert', message });
    });
  }, []);

  const closeDialog = (result: boolean) => {
    setDialog(null);
    dialogResolveRef.current?.(result);
    dialogResolveRef.current = null;
  };

  // 운동일정 목록 조회
  const fetchSchedules = async () => {
    try {
      const response = await apiClient.get('/schedules');
      if (response.data.success) {
        setSchedules(response.data.data || []);
      }
    } catch (error) {
      console.error('운동일정 조회 실패:', error);
    }
  };

  // 게스트 신청 목록 조회
  const fetchGuests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/guests');
      if (response.data.success) {
        setGuests(response.data.data || []);
      }
    } catch (error) {
      console.error('게스트 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchGuests();
  }, []);

  const scheduledDates = schedules.map(s => s.schedule_date);

  // 필터링된 게스트 목록
  const filteredGuests = guests.filter(guest => {
    const matchesSearch = guest.name.includes(searchTerm);
    const matchesStatus = filterStatus === '전체' || guest.status === filterStatus;
    const matchesDate = !selectedDate || guest.selected_date === selectedDate;
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  // 통계
  const stats = {
    total: guests.length,
    pending: guests.filter(g => g.status === '대기').length,
    approved: guests.filter(g => g.status === '승인').length,
    rejected: guests.filter(g => g.status === '거부').length,
  };

  const handleDateClick = (dateStr: string) => {
    // 같은 날짜를 다시 클릭하면 선택 해제 (전체 보기)
    if (selectedDate === dateStr) {
      setSelectedDate('');
    } else {
      setSelectedDate(dateStr);
    }
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    // TODO: 새로운 월의 일정 불러오기
  };

  const handleGuestClick = (guest: Guest) => {
    setSelectedGuest(guest);
    setEditForm({
      name: guest.name,
      phone: guest.phone,
      birth_date: guest.birth_date,
      gender: guest.gender,
      election_status: guest.election_status,
      selected_date: guest.selected_date,
      apply_path: guest.apply_path,
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedGuest) return;
    if (!(await showConfirm('이 게스트 신청을 승인하시겠습니까?'))) return;

    const guest = selectedGuest;

    try {
      const response = await apiClient.put(`/guests/${guest.guest_no}/approve`);
      if (response.data.success) {
        await showAlert('승인되었습니다.');

        fetchGuests();
        setIsModalOpen(false);
        setSelectedGuest(null);

        if (guest.phone && await showConfirm('SMS를 보내시겠습니까?')) {
          try {
            const schedule = schedules.find(s => s.schedule_date === guest.selected_date);
            const location = schedule?.location || '장소 미정';
            const scheduleTime = schedule?.schedule_time || '시간 미정';

            const dateParts = (guest.selected_date || '').split('-');
            const formattedDate = dateParts.length >= 3
              ? `${dateParts[0]}년 ${parseInt(dateParts[1])}월 ${parseInt(dateParts[2])}일`
              : guest.selected_date || '날짜 미정';

            const approverName = user?.name || '관리자';
            const approverRole = user?.admin_role || null;
            const approverTitle = approverRole ? approverRole : approverName;

            const applyCount = guest.apply_count || 1;
            const feeRequired = guest.fee_required || false;

            let smsMessage = `안녕하세요 H_B ${approverTitle} 입니다.

${formattedDate} ${scheduleTime}, ${location}로 게스트 신청 완료되었습니다.

${applyCount}회차 참석하셨습니다.`;

            if (feeRequired) {
              smsMessage += `

2회차부터는 게스트비가 발생됩니다.
게스트비는 운동전에 입금해주세요.

계좌번호  79423097529 카카오뱅크 추도빈`;
            }

            smsMessage += `

감사합니다`;

            const phoneNumber = guest.phone.replace(/-/g, '');
            const smsLink = `sms:${phoneNumber}?body=${encodeURIComponent(smsMessage)}`;
            window.location.href = smsLink;
          } catch (smsError) {
            console.error('SMS 링크 생성 실패:', smsError);
            await showAlert('SMS 링크 생성에 실패했습니다.');
          }
        }
      }
    } catch (error: any) {
      console.error('승인 실패:', error);
      await showAlert(error.response?.data?.detail || '승인에 실패했습니다');
    }
  };

  const handleReject = async () => {
    if (!selectedGuest) return;
    if (!(await showConfirm('이 게스트 신청을 거부하시겠습니까?'))) return;

    try {
      const response = await apiClient.put(`/guests/${selectedGuest.guest_no}/reject`);
      if (response.data.success) {
        await showAlert('거부되었습니다');
        fetchGuests();
        setIsModalOpen(false);
        setSelectedGuest(null);
      }
    } catch (error: any) {
      console.error('거부 실패:', error);
      await showAlert(error.response?.data?.detail || '거부에 실패했습니다');
    }
  };

  const handleDelete = async () => {
    if (!selectedGuest) return;
    if (!(await showConfirm('이 게스트 신청을 삭제하시겠습니까?'))) return;

    try {
      const response = await apiClient.delete(`/guests/${selectedGuest.guest_no}`);
      if (response.data.success) {
        await showAlert('삭제되었습니다');
        fetchGuests();
        setIsModalOpen(false);
        setSelectedGuest(null);
      }
    } catch (error: any) {
      console.error('삭제 실패:', error);
      await showAlert(error.response?.data?.detail || '삭제에 실패했습니다');
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    if (selectedGuest) {
      setEditForm({
        name: selectedGuest.name,
        phone: selectedGuest.phone,
        birth_date: selectedGuest.birth_date,
        gender: selectedGuest.gender,
        election_status: selectedGuest.election_status,
        selected_date: selectedGuest.selected_date,
        apply_path: selectedGuest.apply_path,
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedGuest) return;

    try {
      const response = await apiClient.put(`/guests/${selectedGuest.guest_no}/info`, editForm);
      if (response.data.success) {
        await showAlert('수정되었습니다');
        fetchGuests(); // 목록 새로고침
        setIsEditMode(false);
        // 모달을 닫지 않고 최신 데이터로 업데이트
        const updatedResponse = await apiClient.get(`/guests/${selectedGuest.guest_no}`);
        if (updatedResponse.data.success) {
          setSelectedGuest(updatedResponse.data.data);
        }
      }
    } catch (error: any) {
      console.error('수정 실패:', error);
      await showAlert(error.response?.data?.detail || '수정에 실패했습니다');
    }
  };

  const formatBirthYear = (birthDate: string | undefined): string => {
    if (!birthDate) return '-';
    if (birthDate.length >= 4) {
      return `${birthDate.slice(0, 2)}년생`;
    }
    return birthDate;
  };

  return (
    <DashboardLayout title="게스트 신청 관리">
      <GuestsContainer>
        <Header>
          <Title>게스트 신청 관리</Title>
        </Header>
        
        <StatsRow>
          <StatCard>
            <StatLabel>전체</StatLabel>
            <StatValue>{stats.total}건</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>대기</StatLabel>
            <StatValue>{stats.pending}건</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>승인</StatLabel>
            <StatValue>{stats.approved}건</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>거부</StatLabel>
            <StatValue>{stats.rejected}건</StatValue>
          </StatCard>
          {selectedDate && (
            <StatCard>
              <StatLabel>{selectedDate}</StatLabel>
              <StatValue>{filteredGuests.length}명</StatValue>
            </StatCard>
          )}
        </StatsRow>
        
        <CalendarSection>
          <Calendar
            year={currentYear}
            month={currentMonth}
            scheduledDates={scheduledDates}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
            allowPastDates={true}
          />
          <CalendarInfo>
            ✓ 검은색 테두리 날짜는 운동 일정이 있는 날입니다. 클릭하여 해당 날짜의 게스트를 확인하세요.
            <br />
            ✓ 선택된 날짜를 다시 클릭하면 전체 게스트를 볼 수 있습니다.
          </CalendarInfo>
        </CalendarSection>
        
        <SearchSection>
          <SearchRow>
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="이름으로 검색"
            />
            <Button 
              variant="primary"
              onClick={() => {}}
            >
              검색
            </Button>
          </SearchRow>
          
          <FilterRow>
            <FilterButton 
              active={filterStatus === '전체'}
              onClick={() => setFilterStatus('전체')}
            >
              전체
            </FilterButton>
            <FilterButton 
              active={filterStatus === '대기'}
              onClick={() => setFilterStatus('대기')}
            >
              대기
            </FilterButton>
            <FilterButton 
              active={filterStatus === '승인'}
              onClick={() => setFilterStatus('승인')}
            >
              승인
            </FilterButton>
            <FilterButton 
              active={filterStatus === '거부'}
              onClick={() => setFilterStatus('거부')}
            >
              거부
            </FilterButton>
          </FilterRow>
        </SearchSection>
        
        <GuestsList>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : filteredGuests.length > 0 ? (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>이름</Th>
                    <Th>성별</Th>
                    <Th>선출</Th>
                    <Th>신청자</Th>
                    <Th>참가일</Th>
                    <Th>횟수</Th>
                    <Th>상태</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredGuests.map(guest => (
                    <Tr key={guest.guest_no}>
                      <NameCell onClick={() => handleGuestClick(guest)}>
                        {guest.name}
                      </NameCell>
                      <Td>{guest.gender}</Td>
                      <Td>{guest.election_status}</Td>
                      <Td>{guest.is_direct_apply ? 'Home' : guest.invited_by_name || '-'}</Td>
                      <Td>{guest.selected_date}</Td>
                      <Td>
                        {guest.apply_count || 1}회
                        {guest.fee_required && (
                          <span style={{ color: '#dc3545', fontSize: '0.7rem', marginLeft: '0.25rem' }}>
                            (8천원)
                          </span>
                        )}
                      </Td>
                      <Td>
                        <StatusBadge status={guest.status}>
                          {guest.status}
                        </StatusBadge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyState>검색 결과가 없습니다.</EmptyState>
          )}
        </GuestsList>
        
        {/* 게스트 상세 정보 및 처리 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => {
            setIsModalOpen(false);
            setIsEditMode(false);
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>게스트 신청 상세</ModalTitle>
              <CloseButton onClick={() => {
                setIsModalOpen(false);
                setIsEditMode(false);
              }}>×</CloseButton>
            </ModalHeader>
            
            {selectedGuest && (
              <>
                <DetailRow>
                  <DetailLabel>이름</DetailLabel>
                  {isEditMode ? (
                    <EditInput
                      type="text"
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  ) : (
                    <DetailValue>{selectedGuest.name}</DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>전화번호</DetailLabel>
                  {isEditMode ? (
                    <EditInput
                      type="text"
                      value={editForm.phone || ''}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      placeholder="010-1234-5678"
                    />
                  ) : (
                    <DetailValue>{selectedGuest.phone}</DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>생년월일</DetailLabel>
                  {isEditMode ? (
                    <EditInput
                      type="text"
                      value={editForm.birth_date || ''}
                      onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })}
                      placeholder="YYMMDD"
                    />
                  ) : (
                    <DetailValue>{formatBirthYear(selectedGuest.birth_date)}</DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>성별</DetailLabel>
                  {isEditMode ? (
                    <EditSelect
                      value={editForm.gender || ''}
                      onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                    >
                      <option value="남">남</option>
                      <option value="여">여</option>
                    </EditSelect>
                  ) : (
                    <DetailValue>{selectedGuest.gender}</DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>선출 여부</DetailLabel>
                  {isEditMode ? (
                    <EditSelect
                      value={editForm.election_status || ''}
                      onChange={(e) => setEditForm({ ...editForm, election_status: e.target.value })}
                    >
                      <option value="선출">선출</option>
                      <option value="비선출">비선출</option>
                    </EditSelect>
                  ) : (
                    <DetailValue>{selectedGuest.election_status}</DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>신청 경로</DetailLabel>
                  {isEditMode ? (
                    selectedGuest.is_direct_apply ? (
                      <EditInput
                        type="text"
                        value={editForm.apply_path || ''}
                        onChange={(e) => setEditForm({ ...editForm, apply_path: e.target.value })}
                        placeholder="신청 경로 입력"
                      />
                    ) : (
                      <DetailValue>
                        <strong>{selectedGuest.invited_by_name || '-'}</strong>님이 초대
                      </DetailValue>
                    )
                  ) : (
                    <DetailValue>
                      {selectedGuest.is_direct_apply ? (
                        <>
                          {selectedGuest.apply_path ? (
                            <strong>{selectedGuest.apply_path}</strong>
                          ) : (
                            <strong>Home에서 직접 신청</strong>
                          )}
                        </>
                      ) : (
                        <>
                          <strong>{selectedGuest.invited_by_name || '-'}</strong>님이 초대
                        </>
                      )}
                    </DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>참가 희망일</DetailLabel>
                  {isEditMode ? (
                    <EditInput
                      type="date"
                      value={editForm.selected_date || ''}
                      onChange={(e) => setEditForm({ ...editForm, selected_date: e.target.value })}
                    />
                  ) : (
                    <DetailValue>{selectedGuest.selected_date}</DetailValue>
                  )}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>신청일</DetailLabel>
                  <DetailValue>{selectedGuest.apply_date}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>신청 횟수</DetailLabel>
                  <DetailValue>
                    {selectedGuest.apply_count || 1}회차
                    {selectedGuest.fee_required && (
                      <span style={{ color: '#dc3545', fontWeight: 700, marginLeft: '0.5rem' }}>
                        (게스트 비용 8,000원 필요)
                      </span>
                    )}
                    {!selectedGuest.fee_required && (
                      <span style={{ color: '#0f5132', marginLeft: '0.5rem' }}>
                        (1회차 무료)
                      </span>
                    )}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>현재 상태</DetailLabel>
                  <DetailValue>
                    <StatusBadge status={selectedGuest.status}>
                      {selectedGuest.status}
                    </StatusBadge>
                  </DetailValue>
                </DetailRow>
                
                <ModalActions>
                  {isEditMode ? (
                    <>
                      <Button
                        variant="primary"
                        size="large"
                        fullWidth
                        onClick={handleSaveEdit}
                      >
                        저장
                      </Button>
                      <Button
                        variant="outline"
                        size="large"
                        fullWidth
                        onClick={handleCancelEdit}
                      >
                        취소
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="large"
                        fullWidth
                        onClick={handleEdit}
                      >
                        수정
                      </Button>
                      {selectedGuest.status === '대기' && (
                        <>
                          <Button
                            variant="primary"
                            size="large"
                            fullWidth
                            onClick={handleApprove}
                          >
                            승인
                          </Button>
                          <Button
                            variant="danger"
                            size="large"
                            fullWidth
                            onClick={handleReject}
                          >
                            거부
                          </Button>
                        </>
                      )}
                      {(selectedGuest.status === '거부' || selectedGuest.status === '승인') && (
                        <Button
                          variant="danger"
                          size="large"
                          fullWidth
                          onClick={handleDelete}
                        >
                          삭제
                        </Button>
                      )}
                    </>
                  )}
                </ModalActions>
              </>
            )}
          </ModalContent>
        </ModalOverlay>

        {dialog && (
          <DialogOverlay onClick={() => dialog.type === 'confirm' && closeDialog(false)}>
            <DialogBox onClick={(e) => e.stopPropagation()}>
              <DialogMessage>{dialog.message}</DialogMessage>
              <DialogActions>
                {dialog.type === 'confirm' ? (
                  <>
                    <Button variant="outline" fullWidth onClick={() => closeDialog(false)}>
                      취소
                    </Button>
                    <Button variant="primary" fullWidth onClick={() => closeDialog(true)}>
                      확인
                    </Button>
                  </>
                ) : (
                  <Button variant="primary" fullWidth onClick={() => closeDialog(true)}>
                    확인
                  </Button>
                )}
              </DialogActions>
            </DialogBox>
          </DialogOverlay>
        )}
      </GuestsContainer>
    </DashboardLayout>
  );
};

export default AdminGuests;
